import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Components
import { Header, PDFViewer, BookmarkTabs, UpgradeModal } from './index';

// Utilities
import { renderPage, getCanvasCoordinates, drawRedactionBox } from '../utils/pdfUtils';
import { buildProofCertificateText, buildProofExportContext, downloadJsonFile, downloadTextFile } from '../utils/proofReport';
import {
  fetchUserFiles,
  uploadFile,
  deleteFile,
  saveRedactions,
  fetchPatterns,
  fetchBillingUsage,
  createCheckoutSession,
  fetchPiiSuggestions,
  csrfHeaders,
} from '../utils/apiUtils';

// Tiny valid 1-page PDF ("Sample Redacto File") in base64 to preload as demo
const SAMPLE_PDF_BASE64 =
  'JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlIC9DYXRhbG9nL1BhZ2VzIDIgMCBSPj4KZW5kb2JqCjIgMCBvYmoKPDwvVHlwZSAvUGFnZXMvS2lkcyBbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoKMyAwIG9iago8PC9UeXBlIC9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveCBbMCAwIDYxMiA3OTJdL0NvbnRlbnRzIDQgMCBSPj4KZW5kb2JqCjQgMCBvYmoKPDwvTGVuZ3RoIDQ5Pj4Kc3RyZWFtCkJUIAovRjEgMjQgVGYKMTUwIDc0MCBUZAooSGVsbG8gV29ybGQhKSBUVAplbmRzdHJlYW0KZW5kb2JqCnhyZWYKMAo1CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDEwMSAwMDAwMCBuIAowMDAwMDAwMDU1IDAwMDAwIG4gCjAwMDAwMDAxNTQgMDAwMDAgbiAKMDAwMDAwMDI5MiAwMDAwMCBuIAp0cmFpbGVyCjw8L1Jvb3QgMSAwIFIvU2l6ZSA1Pj4Kc3RhcnR4cmVmCjM5NQolJUVPRgo=';

const HIDDEN_SEEDED_PATTERNS = new Set([
  'dates',
  'patient_names',
  'names',
  'case_numbers',
  'mrn',
  'agency_contacts',
  'addresses',
]);

const SUGGESTION_TYPE_ORDER = [
  'EMAIL',
  'PHONE',
  'NATIONAL_ID',
  'CASE_NUMBER',
  'DATE_OF_BIRTH',
  'ADDRESS',
  'PERSON',
  'SIGNATURE',
  'STAMP',
  'NUMBER',
  'LOCATION',
  'ORG',
];
const ALLOWED_SUGGESTION_TYPES = new Set(SUGGESTION_TYPE_ORDER);

const normalizeSuggestionText = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

const formatSuggestionText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const getSuggestionOccurrenceKey = (suggestion, pageKey, index) => (
  suggestion?.id
  || `${pageKey}:${suggestion?.type || 'pii'}:${index}:${JSON.stringify(suggestion?.box || {})}`
);

const buildSuggestionGroups = (suggestionsByPage = {}) => {
  const groups = new Map();

  Object.entries(suggestionsByPage || {}).forEach(([pageKey, suggestions]) => {
    (suggestions || []).forEach((suggestion, index) => {
      const type = String(suggestion?.type || 'pii').toUpperCase();
      if (!ALLOWED_SUGGESTION_TYPES.has(type)) return;
      const text = formatSuggestionText(suggestion?.text || suggestion?.label || suggestion?.value || '');
      if (!text) {
        return;
      }
      const normalizedText = normalizeSuggestionText(
        text || suggestion?.id || (suggestion?.box ? JSON.stringify(suggestion.box) : '')
      );
      if (!normalizedText) {
        return;
      }
      const groupKey = `${type}:${normalizedText || getSuggestionOccurrenceKey(suggestion, pageKey, index)}`;
      const occurrence = {
        ...suggestion,
        type,
        text,
        page: Number.isFinite(Number(pageKey)) ? Number(pageKey) : suggestion?.page,
        pageKey: String(pageKey),
        occurrenceKey: getSuggestionOccurrenceKey(suggestion, pageKey, index),
        confidence: Number.isFinite(Number(suggestion?.confidence)) ? Number(suggestion.confidence) : 0.9,
        source: suggestion?.source || 'regex',
        reason: suggestion?.reason || '',
      };

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          type,
          text: text || type,
          normalizedText,
          occurrences: [],
          sources: new Set(),
          confidenceTotal: 0,
          reason: suggestion?.reason || '',
        });
      }

      const group = groups.get(groupKey);
      group.occurrences.push(occurrence);
      group.sources.add(occurrence.source);
      group.confidenceTotal += occurrence.confidence || 0;
      if (!group.reason && occurrence.reason) {
        group.reason = occurrence.reason;
      }
    });
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      count: group.occurrences.length,
      pages: Array.from(new Set(group.occurrences.map((occurrence) => occurrence.pageKey))).sort(),
      sources: Array.from(group.sources).sort(),
      averageConfidence: group.occurrences.length ? group.confidenceTotal / group.occurrences.length : 0,
    }))
    .sort((left, right) => {
      const leftType = SUGGESTION_TYPE_ORDER.indexOf(left.type);
      const rightType = SUGGESTION_TYPE_ORDER.indexOf(right.type);
      if (leftType !== rightType) {
        return (leftType === -1 ? SUGGESTION_TYPE_ORDER.length : leftType) - (rightType === -1 ? SUGGESTION_TYPE_ORDER.length : rightType);
      }
      return left.text.localeCompare(right.text);
    });
};

function RedactionApp({ user, onLogout, theme = 'dark', toggleTheme }) {
  // Constants
  const STATE_NOTHING = 0;
  const STATE_PDF_DISPLAYED = 1;

  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const stateDataRef = useRef({ redactions: {} });

  const [state, setState] = useState(STATE_NOTHING);
  const [stateData, setStateData] = useState({ redactions: {} });
  const [files, setFiles] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [isRedactionMode, setIsRedactionMode] = useState(false);
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false);
  const [patternsDrawerOpen, setPatternsDrawerOpen] = useState(false);
  const [currentHistoryNode, setCurrentHistoryNode] = useState(null);
  const [usage, setUsage] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [modalMode, setModalMode] = useState('upgrade');
  const [preset, setPreset] = useState(null);
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [workflowStage, setWorkflowStage] = useState('idle');
  const [piiSuggestions, setPiiSuggestions] = useState({});
  const [suggestionsMeta, setSuggestionsMeta] = useState(null);
  const [proofReport, setProofReport] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeSuggestionGroupKey, setActiveSuggestionGroupKey] = useState(null);
  const [suggestionSourceFilter, setSuggestionSourceFilter] = useState('all');
  const [suggestionConfidenceFilter, setSuggestionConfidenceFilter] = useState('all');
  const PRESET_DETAILS = {
    sample: 'A sample image pattern for trying the pattern workflow. It is not a sensitive-data detector.',
  };
  const PRESET_PATTERN_MAP = {
    sample: ['sample'],
  };
  const REVIEW_MODES = [
    { id: 'sample', label: 'Try a sample pattern', preset: 'sample', detail: 'Try image-pattern redaction on a sample pattern.' },
    { id: 'regex', label: 'Regex review', sourceFilter: 'regex', detail: 'Show pattern-based suggestions only.' },
    { id: 'entity', label: 'Entity review', sourceFilter: 'ner', detail: 'Show human-reviewed entity matches only.' },
  ];

  const handleApplyPreset = async (presetOverride = preset) => {
    const activePreset = presetOverride || preset;
    if (!activePreset) return;
    try {
      const wanted = PRESET_PATTERN_MAP[activePreset] || [];
      await fetch('/api/patterns/ensure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeaders()) },
        credentials: 'include',
        body: JSON.stringify({ patterns: wanted }),
      });
      const available = await fetchPatterns();
      const present = wanted.filter((w) => available.includes(w));
      setPatternsDrawerOpen(true);
      if (present.length === 0) {
        setToast({ type: 'warn', message: 'Preset loaded, but no patterns were visible after refresh.' });
      } else {
        setToast({ type: 'success', message: `Preset ready. Found ${present.length} pattern(s).` });
        if (stateData.currentPdfId && stateData.pdfPath && present.length) {
          await applyPatternsToCurrent(present);
        }
      }
    } catch (e) {
      console.error('Preset load failed', e);
      setToast({ type: 'error', message: 'Could not load preset patterns right now.' });
    }
  };
  const TOUR_STEPS = [
      {
        title: '1) Drop a PDF',
        body: 'Upload a PDF in the left panel. It is uploaded over a secure connection and prepped for redaction.',
      },
    {
      title: '2) Apply patterns',
      body: 'Use saved patterns or create new ones on the right to auto-select sensitive areas.',
    },
    {
      title: '3) Review & export',
      body: 'Preview pages in the center, then export a redacted PDF. Every action is audit-logged.',
    },
    {
      title: '4) See history',
      body: 'Open the Dashboard for stats and the full audit log. Download CSV if needed.',
    },
  ];
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [hotspot, setHotspot] = useState(null);
  const [toast, setToast] = useState(null);

  const totalFiles = files.length;
  const todayActions = logs.filter((log) => {
    const d = new Date(log.ts || log.timestamp || log.created_at || Date.now());
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }).length;

  useEffect(() => {
    stateDataRef.current = stateData;
  }, [stateData]);

  const mergeRedactionBox = useCallback((pageIndex, redactionBox, { addHistory = true } = {}) => {
    if (!Number.isFinite(pageIndex) || !redactionBox) return;
    const newRedactions = { ...(stateDataRef.current.redactions || {}) };
    if (!newRedactions[pageIndex]) {
      newRedactions[pageIndex] = [];
    }
    const existing = newRedactions[pageIndex].some((box) => (
      box?.x?.[0] === redactionBox.x?.[0]
      && box?.x?.[1] === redactionBox.x?.[1]
      && box?.y?.[0] === redactionBox.y?.[0]
      && box?.y?.[1] === redactionBox.y?.[1]
    ));
    if (existing) return;
    newRedactions[pageIndex].push(redactionBox);

    if (addHistory) {
      const newHistoryNode = {
        redactions: newRedactions,
        previous: currentHistoryNode,
        next: null,
      };
      if (currentHistoryNode) {
        currentHistoryNode.next = newHistoryNode;
      }
      setCurrentHistoryNode(newHistoryNode);
    }

    setStateData((prevState) => ({
      ...prevState,
      redactions: newRedactions,
    }));
    renderPage(currentPage, stateDataRef.current.pathWithoutExt, canvasRef, {
      ...stateDataRef.current,
      redactions: newRedactions,
    });
  }, [canvasRef, currentHistoryNode, currentPage]);

  const showPDF = useCallback((file) => {
    console.log('Showing PDF:', file.path, 'with', file.pages, 'pages');
    if (!file.path) {
      alert('No PDF loaded.');
      return;
    }
    const normalizedPath = String(file.path).replace(/\\/g, '/');
    let userIdFromPath = null;
    const pathParts = normalizedPath.split('/');
    const rootIndex = pathParts.findIndex(part => part === 'pdf' || part === 'redacted');
    if (rootIndex !== -1 && pathParts.length > rootIndex + 1) {
      userIdFromPath = pathParts[rootIndex + 1];
    } else if (pathParts.length >= 2) {
      userIdFromPath = pathParts[0];
    }
    let renderPathWithoutExt = normalizedPath;
    if (normalizedPath.includes('/redacted/')) {
      const redactedIndex = pathParts.indexOf('redacted');
      const redactedUserId = redactedIndex !== -1 && pathParts[redactedIndex + 1] ? pathParts[redactedIndex + 1] : userIdFromPath || '0';
      const outputFilename = pathParts[pathParts.length - 1].replace(/\.pdf$/i, '');
      renderPathWithoutExt = `${redactedUserId}/${outputFilename}`;
    }
    setState(STATE_PDF_DISPLAYED);
    setWorkflowStage('ready');
      setStateData((prevState) => ({
      ...prevState,
      pdfPath: normalizedPath,
      currentPdfId: file.id,
      redactions: {},
      pathWithoutExt: renderPathWithoutExt,
      userId: userIdFromPath,
    }));
    setTotalPages(file.pages);
    setCurrentPage(0);
    setCurrentHistoryNode({
      redactions: {},
      previous: null,
      next: null
    });
    setPiiSuggestions({});
    setSuggestionsMeta(null);
    setProofReport(null);
    setActiveSuggestionGroupKey(null);
  }, []);

  useEffect(() => {
    if (!location.state?.fileId || files.length === 0) {
      return;
    }
    const uploadedFile = files.find((file) => String(file.id) === String(location.state.fileId));
    if (uploadedFile) {
      showPDF(uploadedFile);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [files, location.state, showPDF]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = overlayCanvasRef.current;
    if (canvas && overlay) {
      overlay.width = canvas.width;
      overlay.height = canvas.height;
      
      // Sync CSS dimensions to match display size
      overlay.style.width = canvas.style.width || `${canvas.width}px`;
      overlay.style.height = canvas.style.height || `${canvas.height}px`;
    }
  }, []);

  // Update overlay canvas size and position when main canvas changes
  useEffect(() => {
    const mainCanvas = canvasRef.current;
    const overlay = overlayCanvasRef.current;
    if (mainCanvas && overlay) {
      overlay.width = mainCanvas.width;
      overlay.height = mainCanvas.height;
      
      overlay.style.width = mainCanvas.style.width || `${mainCanvas.width}px`;
      overlay.style.height = mainCanvas.style.height || `${mainCanvas.height}px`;
    }
  }, [currentPage]); 

  useEffect(() => {
    if (state === STATE_PDF_DISPLAYED && stateData.pathWithoutExt) {
      console.log('Rendering PDF page:', currentPage, 'Path:', stateData.pathWithoutExt);
      renderPage(currentPage, stateData.pathWithoutExt, canvasRef, stateDataRef.current);
    }
  }, [currentPage, state, stateData.pathWithoutExt]); 

  useEffect(() => {
    const loadSuggestions = async () => {
      if (state !== STATE_PDF_DISPLAYED || !stateData.currentPdfId) {
        setPiiSuggestions({});
        setSuggestionsMeta(null);
        return;
      }
      setSuggestionsLoading(true);
      try {
        const result = await fetchPiiSuggestions(stateData.currentPdfId);
        if (result.success) {
          setPiiSuggestions(result.suggestions || {});
          setSuggestionsMeta(result.scanInfo || null);
          if (result.scanInfo?.warnings?.length) {
            setToast({
              type: 'info',
              message: result.scanInfo.warnings[0],
            });
          }
        } else {
          setPiiSuggestions({});
          setSuggestionsMeta(null);
          if (result.error) {
            console.warn('PII suggestion load failed:', result.error);
          }
        }
      } finally {
        setSuggestionsLoading(false);
      }
    };
    loadSuggestions();
  }, [state, stateData.currentPdfId]);

  useEffect(() => {
    return () => {
    };
  }, []);

  const fetchFiles = useCallback(async () => {
    console.log('📁 Fetching user files...');
    const nextFiles = await fetchUserFiles();
    console.log('📁 Files fetched:', nextFiles.length, 'files');
    setFiles(nextFiles);
  }, []);

  const fetchPatternsData = useCallback(async () => {
    const nextPatterns = await fetchPatterns();
    setPatterns((nextPatterns || []).filter((p) => !HIDDEN_SEEDED_PATTERNS.has(p)));
  }, []);

  const loadUsage = useCallback(async () => {
    const result = await fetchBillingUsage();
    if (result.success) {
      setUsage(result.data);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const billingStatus = params.get('billing');

    if (billingStatus === 'success') {
      loadUsage();
      alert('Upgrade successful. Your plan is now active.');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (billingStatus === 'cancelled') {
      alert('Checkout was cancelled. You can upgrade any time from the editor.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, loadUsage]);

  useEffect(() => {
    fetchFiles();
    fetchPatternsData();
    loadUsage();
  }, [user, fetchFiles, fetchPatternsData, loadUsage]);

  useEffect(() => {
    if (location.state?.preset) {
      setPreset(location.state.preset);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  const handleStartTour = () => {
    setTourStep(0);
    setShowTour(true);
    setHotspot('upload');
  };

  const handleNextTour = () => {
    setTourStep((s) => {
      const next = Math.min(s + 1, TOUR_STEPS.length - 1);
      setHotspot(['upload','patterns','export','dashboard'][next] || null);
      return next;
    });
  };

  const handlePrevTour = () => {
    setTourStep((s) => {
      const prev = Math.max(s - 1, 0);
      setHotspot(['upload','patterns','export','dashboard'][prev] || null);
      return prev;
    });
  };

  const handleCloseTour = () => { setShowTour(false); setHotspot(null); };

  const handleLoadSample = async () => {
    try {
      setIsLoading(true);
      setWorkflowStage('uploading');
      setProofReport(null);
      setToast({ type: 'info', message: 'Loading the sample PDF…' });
      const byteCharacters = atob(SAMPLE_PDF_BASE64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const file = new File([blob], 'Sample_Redacto_File.pdf', { type: 'application/pdf' });

      const formData = new FormData();
      formData.append('file', file);

      const result = await uploadFile(formData);
      if (result.success) {
        setWorkflowStage('processing');
        setToast({ type: 'info', message: 'Sample uploaded. Preparing the document…' });
        await fetchFiles();
        setWorkflowStage('ready');
        const sample = (files || []).find((f) => (f.title || '').includes('Sample_Redacto_File'));
        if (sample) {
          showPDF(sample);
          setToast({ type: 'success', message: 'Sample PDF is ready.' });
        } else {
          setToast({ type: 'warn', message: 'Sample uploaded, but it did not appear in the file list yet.' });
        }
      } else {
        setWorkflowStage('idle');
        setToast({ type: 'error', message: result.error || 'Failed to load sample file.' });
      }
    } catch (err) {
      console.error('Sample load failed:', err);
      setWorkflowStage('idle');
      setToast({ type: 'error', message: 'Failed to load sample file.' });
    }
    setIsLoading(false);
  };

  const applyPatternsToCurrent = async (patternNames = []) => {
    if (!patternNames.length || !stateData.currentPdfId || !stateData.pdfPath) return;
    setIsLoading(true);
    setWorkflowStage('processing');
    try {
      const res = await fetch(`/api/patterns/${patternNames.join(',')}/redact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeaders()) },
        credentials: 'include',
        body: JSON.stringify({ pdfPath: stateData.pdfPath, file_id: stateData.currentPdfId }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || res.statusText || 'Unable to apply patterns');
      }
      const refreshed = await fetchUserFiles();
      setFiles(refreshed);
      const match = data?.id
        ? refreshed.find((f) => String(f.id) === String(data.id))
        : refreshed.find((f) => f.title === data?.filename);
      if (match) {
        showPDF(match);
      }
      setToast({ type: 'success', message: 'Sample pattern applied. Opened the redacted file.' });
      setWorkflowStage('ready');
    } catch (err) {
      console.error('applyPatternsToCurrent error', err);
      setToast({ type: 'error', message: `Could not apply preset patterns right now. ${err.message || ''}` });
      setWorkflowStage('ready');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await fetch('/api/logs');
        if (!res.ok) throw new Error('');
        const data = await res.json();
        const normalized = (Array.isArray(data?.logs) ? data.logs : []).map((log) => ({
          ...log,
          file: log.file || log.file_name || log.file_id,
          ts: log.ts || log.created_at || log.timestamp,
          user: log.user || log.username,
        }));
        setLogs(normalized);
      } catch (e) {
        setLogs([
          { id: 1, ts: new Date().toISOString(), action: 'upload', file: 'Sample_Redacto_File.pdf', user: user?.login || 'me' },
          { id: 2, ts: new Date().toISOString(), action: 'redact', file: 'Sample_Redacto_File.pdf', user: user?.login || 'me' },
        ]);
      }
    };
    fetchLogs();
  }, [user]);

  const handleUpload = async (input) => {
    const isEvent = Boolean(input?.preventDefault);
    if (isEvent) {
      input.preventDefault();
    }

    const selectedFiles = Array.isArray(input?.files)
      ? input.files
      : input?.target?.files
        ? Array.from(input.target.files)
        : input?.target?.elements?.file?.files
          ? Array.from(input.target.elements.file.files)
          : [];

    if (!selectedFiles.length) {
      setToast({ type: 'info', message: 'Select a file first.' });
      return;
    }

    setIsLoading(true);
    setWorkflowStage('uploading');
    setProofReport(null);

    const uploadQueue = selectedFiles.filter(Boolean);
    const uploadedNames = [];

    try {
      setWorkflowStage(uploadQueue.length > 1 ? 'uploading' : 'processing');
      setToast({
        type: 'info',
        message: uploadQueue.length > 1
          ? `Uploading ${uploadQueue.length} files…`
          : 'Upload complete. Preparing the PDF…',
      });

      for (const file of uploadQueue) {
        const formData = new FormData();
        formData.append('file', file);
        const result = await uploadFile(formData);
        if (!result.success) {
          throw new Error(result.error || `Failed to upload ${file.name}`);
        }
        uploadedNames.push(file.name);
      }

      await fetchFiles();

      if (isEvent && input.target?.tagName === 'FORM') {
        input.target.reset();
      } else if (isEvent && input.target?.type === 'file') {
        input.target.value = '';
      }

      setWorkflowStage('ready');
      setToast({
        type: 'success',
        message: uploadQueue.length > 1
          ? `Uploaded ${uploadedNames.length} files. Your batch is ready.`
          : 'PDF uploaded and ready.',
      });
    } catch (error) {
      setWorkflowStage('idle');
      setToast({ type: 'error', message: 'Upload failed: ' + (error?.message || '') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedactStart = () => {
    if (state !== STATE_PDF_DISPLAYED) {
      setToast({ type: 'info', message: 'Select a file first.' });
      return;
    }
    const nextMode = !isRedactionMode;
    setIsRedactionMode(nextMode); // Toggle redaction mode
    setToast({
      type: 'info',
      message: nextMode ? 'Redaction mode on. Draw on the page to redact.' : 'Redaction mode off.',
    });
  };

  const handleMouseDown = (event) => {
    console.log('Mouse down - Redaction mode:', isRedactionMode);
    if (!isRedactionMode) return;
    
    const coords = getCanvasCoordinates(event, canvasRef, stateDataRef.current);
    console.log('Mouse down coordinates:', coords);
    if (!coords) return;

    setIsDrawing(true);
    setStartPoint(coords);
  };

  const handleMouseMove = (event) => {
    if (!isDrawing || !startPoint || !isRedactionMode) return;

    const coords = getCanvasCoordinates(event, canvasRef, stateDataRef.current);
    if (!coords) return;

    // Use the drawRedactionBox utility for preview
    drawRedactionBox(startPoint, coords, overlayCanvasRef, canvasRef, stateData, true);
  };

  const handleMouseUp = (event) => {
    if (!isDrawing || !startPoint || !isRedactionMode) return;

    const coords = getCanvasCoordinates(event, canvasRef, stateDataRef.current);
    if (!coords) return;

    // Calculate final redaction coordinates
    const x1 = Math.min(startPoint.x, coords.x);
    const y1 = Math.min(startPoint.y, coords.y);
    const x2 = Math.max(startPoint.x, coords.x);
    const y2 = Math.max(startPoint.y, coords.y);
    const width = x2 - x1;
    const height = y2 - y1;
    console.log('x1', x1, 'x2', x2, 'y1', y1, 'y2', y2)

    // Only add redaction if it has significant size
    if (width > 0.005 && height > 0.005) {
      const newRedaction = {
        x: [x1, x2],
        y: [y1, y2]
      };
      mergeRedactionBox(currentPage, newRedaction);
    }

    // Clear overlay
    const overlay = overlayCanvasRef.current;
    if (overlay) {
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, overlay.width, overlay.height);
    }

    setIsDrawing(false);
    setStartPoint(null);
  };

  const handleUndo = () => {
    if (currentHistoryNode && currentHistoryNode.previous) {
      setCurrentHistoryNode(currentHistoryNode.previous);
      setStateData(prevState => ({
        ...prevState,
        redactions: currentHistoryNode.previous.redactions
      }));
      renderPage(currentPage, stateData.pathWithoutExt, canvasRef, { ...stateData, redactions: currentHistoryNode.previous.redactions });
    }
  };

  const handleRedo = () => {
    if (currentHistoryNode && currentHistoryNode.next) {
      setCurrentHistoryNode(currentHistoryNode.next);
      setStateData(prevState => ({
        ...prevState,
        redactions: currentHistoryNode.next.redactions
      }));
      renderPage(currentPage, stateData.pathWithoutExt, canvasRef, { ...stateData, redactions: currentHistoryNode.next.redactions });
    }
  };

  const removeSuggestionGroupFromState = (groupKey) => {
    setPiiSuggestions((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([pageKey, suggestions]) => {
        const filteredSuggestions = (suggestions || []).filter((suggestion, index) => {
          const type = String(suggestion?.type || 'pii').toUpperCase();
          const text = formatSuggestionText(suggestion?.text || suggestion?.label || suggestion?.value || '');
          const normalizedText = normalizeSuggestionText(text || (suggestion?.id ? suggestion.id : suggestion?.box ? JSON.stringify(suggestion.box) : ''));
          const nextGroupKey = `${type}:${normalizedText || getSuggestionOccurrenceKey(suggestion, pageKey, index)}`;
          return nextGroupKey !== groupKey;
        });
        if (filteredSuggestions.length > 0) {
          next[pageKey] = filteredSuggestions;
        }
      });
      return next;
    });
    setActiveSuggestionGroupKey((current) => (current === groupKey ? null : current));
  };

  const acceptSuggestionGroup = (group) => {
    if (!group?.occurrences?.length) return;
    const nextRedactions = { ...(stateDataRef.current.redactions || {}) };
    let changed = false;

    group.occurrences.forEach((occurrence) => {
      const pageIndex = Number(occurrence.page ?? occurrence.pageKey);
      if (!Number.isFinite(pageIndex) || !occurrence?.box) {
        return;
      }
      if (!nextRedactions[pageIndex]) {
        nextRedactions[pageIndex] = [];
      }
      const exists = nextRedactions[pageIndex].some((box) => (
        box?.x?.[0] === occurrence.box?.x?.[0]
        && box?.x?.[1] === occurrence.box?.x?.[1]
        && box?.y?.[0] === occurrence.box?.y?.[0]
        && box?.y?.[1] === occurrence.box?.y?.[1]
      ));
      if (!exists) {
        nextRedactions[pageIndex].push(occurrence.box);
        changed = true;
      }
    });

    if (!changed) {
      removeSuggestionGroupFromState(group.groupKey);
      return;
    }

    const newHistoryNode = {
      redactions: nextRedactions,
      previous: currentHistoryNode,
      next: null,
    };
    if (currentHistoryNode) {
      currentHistoryNode.next = newHistoryNode;
    }
    setCurrentHistoryNode(newHistoryNode);
    setStateData((prevState) => ({
      ...prevState,
      redactions: nextRedactions,
    }));
    renderPage(currentPage, stateDataRef.current.pathWithoutExt, canvasRef, {
      ...stateDataRef.current,
      redactions: nextRedactions,
    });
    removeSuggestionGroupFromState(group.groupKey);
    setToast({ type: 'success', message: `Accepted ${group.count} suggestion(s).` });
  };

  const dismissSuggestionGroup = (group) => {
    if (!group?.groupKey) return;
    removeSuggestionGroupFromState(group.groupKey);
    setToast({ type: 'info', message: `Dismissed ${group.count} suggestion(s).` });
  };

  const acceptAllSuggestions = () => {
    const groups = buildSuggestionGroups(piiSuggestions);
    if (!groups.length) {
      setToast({ type: 'info', message: 'No suggestions to accept.' });
      return;
    }
    
    const totalOccurrences = groups.reduce((sum, g) => sum + (g.count || 0), 0);
    
    const confirmed = window.confirm(
      `This will redact ${totalOccurrences} occurrences across ${groups.length} groups.\n\n` +
      `Detected types: EMAIL, PHONE, NUMBER.\n\n` +
      `Do you want to proceed?`
    );
    
    if (!confirmed) {
      return;
    }

    // Apply all suggestion occurrences (all groups)
    let nextRedactions = { ...(stateDataRef.current.redactions || {}) };
    let changed = false;

    groups.forEach((group) => {
      (group.occurrences || []).forEach((occurrence) => {
        const pageIndex = Number(occurrence.page ?? occurrence.pageKey);
        if (!Number.isFinite(pageIndex) || !occurrence?.box) return;

        if (!nextRedactions[pageIndex]) nextRedactions[pageIndex] = [];

        const exists = nextRedactions[pageIndex].some((box) => (
          box?.x?.[0] === occurrence.box?.x?.[0]
          && box?.x?.[1] === occurrence.box?.x?.[1]
          && box?.y?.[0] === occurrence.box?.y?.[0]
          && box?.y?.[1] === occurrence.box?.y?.[1]
        ));

        if (!exists) {
          nextRedactions[pageIndex].push(occurrence.box);
          changed = true;
        }
      });
    });

    if (!changed) {
      setToast({ type: 'info', message: 'No suggestions to accept.' });
      return;
    }


    const newHistoryNode = {
      redactions: nextRedactions,
      previous: currentHistoryNode,
      next: null,
    };
    if (currentHistoryNode) {
      currentHistoryNode.next = newHistoryNode;
    }
    setCurrentHistoryNode(newHistoryNode);
    setStateData((prevState) => ({
      ...prevState,
      redactions: nextRedactions,
    }));
    renderPage(currentPage, stateDataRef.current.pathWithoutExt, canvasRef, {
      ...stateDataRef.current,
      redactions: nextRedactions,
    });
    setPiiSuggestions({});
    setActiveSuggestionGroupKey(null);
    setToast({ type: 'success', message: 'Accepted all suggested redactions.' });
  };

  const dismissAllSuggestions = () => {
    setPiiSuggestions({});
    setActiveSuggestionGroupKey(null);
    setToast({ type: 'info', message: 'Dismissed all suggestions.' });
  };

  const suggestionGroups = buildSuggestionGroups(piiSuggestions);
  const suggestionCount = suggestionGroups.length;
  const confidenceThresholdMap = {
    all: 0,
    medium: 0.75,
    high: 0.9,
    strict: 0.96,
  };
  const visibleSuggestionGroups = suggestionGroups.filter((group) => {
    const sourceMatches = suggestionSourceFilter === 'all' || group.source === suggestionSourceFilter;
    const threshold = confidenceThresholdMap[suggestionConfidenceFilter] || 0;
    const confidenceMatches = (group.averageConfidence || 0) >= threshold;
    return sourceMatches && confidenceMatches;
  });
  const visibleSuggestionSummary = visibleSuggestionGroups.reduce((summary, group) => {
    summary.groups += 1;
    summary.occurrences += group.count || 0;
    return summary;
  }, { groups: 0, occurrences: 0 });
  const workspaceGuide = state === STATE_NOTHING
    ? {
        tag: 'Start here',
        title: 'Load a document or sample file',
        body: 'Drag in a PDF, choose Load sample, or open a recent file. We’ll guide you from upload to export in a few clear steps.',
      }
    : workflowStage === 'uploading'
      ? {
          tag: 'Working',
          title: 'Uploading and preparing your file',
          body: 'The document is being added to your workspace. Once it finishes, you can review and redact with confidence.',
        }
      : suggestionCount > 0
        ? {
            tag: 'Smart assist',
            title: `${suggestionCount} suggestion group${suggestionCount === 1 ? '' : 's'} ready`,
            body: 'Use the suggested redactions panel to review likely matches faster and keep the process consistent.',
          }
        : {
            tag: 'Next step',
            title: 'Review the page, then export',
            body: 'Draw redactions manually, check the audit trail, and export when the document looks right.',
          };
  const handleMouseLeave = () => {
    if (isDrawing) {
      // Clear overlay when mouse leaves
      const overlay = overlayCanvasRef.current;
      if (overlay) {
        const ctx = overlay.getContext('2d');
        ctx.clearRect(0, 0, overlay.width, overlay.height);
      }
      setIsDrawing(false);
      setStartPoint(null);
    }
  };

  const savePDF = async () => {
    if (state !== STATE_PDF_DISPLAYED) {
      setToast({ type: 'error', message: 'No PDF loaded.' });
      return;
    }

    setIsLoading(true);
    setWorkflowStage('processing');
    try {
      // Convert page-based redactions to the format expected by the backend
      const redactionsForBackend = {};
      Object.keys(stateData.redactions).forEach(pageNum => {
        const pageRedactions = stateData.redactions[pageNum];
        if (pageRedactions && pageRedactions.length > 0) {
          redactionsForBackend[pageNum] = pageRedactions;
        }
      });

      const result = await saveRedactions(stateData.currentPdfId, redactionsForBackend);
      if (result.success) {
        if (result.usage) {
          setUsage(result.usage);
        }
        if (result.proof_report) {
          setProofReport(result.proof_report);
        }
        setToast({ type: 'success', message: 'PDF saved successfully.' });
        if (result.warnings && result.warnings.length) {
          console.warn('Redaction warnings:', result.warnings);
          setToast({ type: 'warn', message: `Saved with warnings on ${result.warnings.length} page(s)` });
        }
        // Force-download using fetch + blob so the file is reliably downloaded on Save.
        const downloadHref = result.download_url.startsWith('http')
          ? result.download_url
          : new URL(result.download_url, window.location.origin).toString();
        const downloadResponse = await fetch(downloadHref, {
          method: 'GET',
          credentials: 'include',
        });
        if (!downloadResponse.ok) {
          throw new Error(`Download failed (HTTP ${downloadResponse.status})`);
        }

        const pdfBlob = await downloadResponse.blob();
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = result.filename || 'redacted.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        setWorkflowStage('ready');
      } else {
        if (result.warnings && result.warnings.length) {
          const details = result.warnings.join(' | ');
          setToast({ type: 'error', message: `Redaction failed: ${details}` });
          return;
        }
        if (result.usage) {
          setUsage(result.usage);
        }
        if (result.loginRequired) {
          openUpgradeModal(
            result.error || 'You have used all 10 free guest redactions. Continue with Google to keep redacting files.',
            'login'
          );
        } else if (result.upgradeRequired) {
          openUpgradeModal(result.error || 'You have reached your free redaction limit. Upgrade to continue.');
        } else {
          setToast({ type: 'error', message: 'Failed to save PDF: ' + (result.error || '') });
        }
        setWorkflowStage('ready');
      }
    } catch (error) {
      console.error('Error saving PDF:', error);
      setToast({ type: 'error', message: 'Failed to save PDF' });
      setWorkflowStage('ready');
    }
    setIsLoading(false);
  };

  const changePage = (delta) => {
    const newPage = currentPage + delta;
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleDelete = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      const result = await deleteFile(fileId);
      if (result.success) {
        await fetchFiles();
        if (stateData.currentPdfId === fileId) {
          setState(STATE_NOTHING);
          setStateData({ redactions: {} });
          setWorkflowStage('idle');
        }
      } else {
        setToast({ type: 'error', message: 'Failed to delete file: ' + result.error });
      }
    }
  };

  const openUpgradeModal = useCallback((message = '', mode = 'upgrade') => {
    setModalMode(mode);
    setUpgradeMessage(message);
    setCheckoutError('');
    setUpgradeModalOpen(true);
  }, []);

  const handleUpgradeCheckout = useCallback(async () => {
    if (modalMode === 'login') {
      navigate('/login');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError('');
    const result = await createCheckoutSession(window.location.origin);
    if (result.success && result.data?.checkout_url) {
      window.location.href = result.data.checkout_url;
      return;
    }
    setCheckoutError(result.error || 'Unable to start checkout right now.');
    setCheckoutLoading(false);
  }, [modalMode, navigate]);

  return (
    <React.Fragment>
      <div className="redaction-shell">
        <Header user={user} handleLogout={onLogout} theme={theme} toggleTheme={toggleTheme} usage={usage} />
        <div className="editor-bar">
          <div className="editor-bar-left">
            <span className="editor-title">Redaction console</span>
            {/* Navigation is provided by the tabs in the sidebar; avoid duplicate labels here */}
            <button type="button" className="editor-btn" onClick={() => setLogDrawerOpen(true)}>Activity log</button>
            <button type="button" className="editor-btn ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
          </div>
          <div className="editor-bar-right">
            <button type="button" className={`editor-btn primary ${hotspot === 'upload' ? 'hotspot' : ''}`} onClick={handleLoadSample}>
              Load sample
            </button>
            <button type="button" className={`editor-btn ${hotspot === 'tour' ? 'hotspot' : ''}`} onClick={handleStartTour}>
              Start tour
            </button>
            <div className="editor-metric">
              <span>Files</span>
              <strong>{totalFiles}</strong>
            </div>
            <div className="editor-metric">
              <span>Actions</span>
              <strong>{todayActions}</strong>
            </div>
          </div>
        </div>

        <div className="workspace-guide">
          <div className="workspace-guide-copy">
            <span className="workspace-guide-tag">{workspaceGuide.tag}</span>
            <strong>{workspaceGuide.title}</strong>
            <p>{workspaceGuide.body}</p>
          </div>
          <div className="workspace-guide-actions">
            <button type="button" className="workspace-guide-btn primary" onClick={handleLoadSample}>
              Load sample
            </button>
            <button type="button" className="workspace-guide-btn" onClick={handleStartTour}>
              Start tour
            </button>
            <button type="button" className="workspace-guide-btn ghost" onClick={() => navigate('/dashboard')}>
              Open dashboard
            </button>
          </div>
        </div>

        <div className="preset-mode-strip">
          <div className="preset-mode-copy">
            <span className="workspace-guide-tag">Review tools</span>
            <strong>Sample patterns and filter modes</strong>
            <p>Try a sample image pattern, or narrow the review panel by source and confidence.</p>
          </div>
          <div className="preset-mode-actions">
            {REVIEW_MODES.map((mode) => (
              <button
                key={mode.id}
                type="button"
                className="preset-mode-btn"
                onClick={async () => {
                  if (mode.preset) {
                    setSuggestionSourceFilter('all');
                    setPreset(mode.preset);
                    await handleApplyPreset(mode.preset);
                    return;
                  }
                  setSuggestionSourceFilter(mode.sourceFilter || 'all');
                  setPatternsDrawerOpen(true);
                  setToast({ type: 'info', message: `Showing ${mode.label.toLowerCase()} for faster review.` });
                }}
              >
                <strong>{mode.label}</strong>
                <span>{mode.detail}</span>
              </button>
            ))}
          </div>
        </div>

        {preset && (
          <div className="editor-preset-inline">
            <div>
              <div className="preset-label">Sample pattern active</div>
              <div className="preset-copy">{PRESET_DETAILS[preset] || 'Pattern suggestions loaded.'}</div>
            </div>
            <div className="preset-actions">
              <button className="qs-btn" type="button" onClick={() => handleApplyPreset()}>Load suggestions</button>
              <button className="qs-btn ghost" type="button" onClick={() => setPreset(null)}>Clear</button>
            </div>
          </div>
        )}

        <div className="main-content">
          {/* Main content area with viewer and tabs */}
          <div className="pdf-content-area">
          <BookmarkTabs 
            files={files} 
            patterns={patterns}
            isLoading={isLoading} 
            handleUpload={handleUpload} 
            showPDF={showPDF}
            handleDelete={handleDelete}
            currentPdfId={stateData.currentPdfId}
            currentPage={currentPage}
            stateData={stateData} 
            setSessionsDrawerOpen={setSessionsDrawerOpen}
            setPatternsDrawerOpen={setPatternsDrawerOpen}
            fetchPatternsData={fetchPatternsData}
            sessionsDrawerOpen={sessionsDrawerOpen}
            patternsDrawerOpen={patternsDrawerOpen}
          />
          
          <PDFViewer
            state={state}
            stateData={stateData}
            currentPage={currentPage}
            totalPages={totalPages}
            changePage={changePage}
            isRedactionMode={isRedactionMode}
            handleRedactStart={handleRedactStart}
            savePDF={savePDF}
            isLoading={isLoading}
            canvasRef={canvasRef}
            overlayCanvasRef={overlayCanvasRef}
            handleMouseDown={handleMouseDown}
            handleMouseMove={handleMouseMove}
            handleMouseUp={handleMouseUp}
            handleMouseLeave={handleMouseLeave}
            handleUndo={handleUndo}
            handleRedo={handleRedo}
          currentHistoryNode={currentHistoryNode}
          STATE_NOTHING={STATE_NOTHING}
          STATE_PDF_DISPLAYED={STATE_PDF_DISPLAYED}
            sessionsDrawerOpen={sessionsDrawerOpen}
            setSessionsDrawerOpen={setSessionsDrawerOpen}
            patternsDrawerOpen={patternsDrawerOpen}
            setPatternsDrawerOpen={setPatternsDrawerOpen}
            files={files}
            patterns={patterns}
            handleUpload={handleUpload}
            showPDF={showPDF}
          handleDelete={handleDelete}
          fetchPatternsData={fetchPatternsData}
          setFiles={setFiles}
          hotspot={hotspot}
          setToast={setToast}
          workflowStage={workflowStage}
          piiSuggestions={piiSuggestions}
          suggestionsMeta={suggestionsMeta}
            proofReport={proofReport}
            suggestionGroups={suggestionGroups}
            visibleSuggestionGroups={visibleSuggestionGroups}
            suggestionSourceFilter={suggestionSourceFilter}
            setSuggestionSourceFilter={setSuggestionSourceFilter}
            suggestionConfidenceFilter={suggestionConfidenceFilter}
            setSuggestionConfidenceFilter={setSuggestionConfidenceFilter}
            suggestionsLoading={suggestionsLoading}
            activeSuggestionGroupKey={activeSuggestionGroupKey}
            setActiveSuggestionGroupKey={setActiveSuggestionGroupKey}
            acceptSuggestionGroup={acceptSuggestionGroup}
            dismissSuggestionGroup={dismissSuggestionGroup}
            acceptAllSuggestions={acceptAllSuggestions}
            dismissAllSuggestions={dismissAllSuggestions}
            onDownloadProofReport={() => {
              if (!proofReport) return;
              const exportContext = {
                exportedAt: new Date().toISOString(),
                fileName: proofReport?.file?.name || 'redaction-proof',
                fileId: stateData?.currentPdfId || null,
                user: user?.login || user?.username || user?.email || null,
                reviewMode: suggestionSourceFilter === 'ner'
                  ? 'entity review'
                  : suggestionSourceFilter === 'regex'
                    ? 'regex review'
                    : 'policy review',
                activeFilters: [
                  suggestionSourceFilter === 'all' ? null : `source:${suggestionSourceFilter}`,
                  suggestionConfidenceFilter === 'all' ? null : `confidence:${suggestionConfidenceFilter}`,
                ].filter(Boolean),
                suggestionSummary: {
                  groups: visibleSuggestionSummary.groups,
                  occurrences: visibleSuggestionSummary.occurrences,
                  policyPack: preset || null,
                },
                pageCount: totalPages || proofReport?.file?.page_count || 0,
              };
              const payload = buildProofExportContext(proofReport, exportContext);
              const baseName = String(proofReport?.file?.name || 'redaction-proof').replace(/\.pdf$/i, '');
              downloadJsonFile(`${baseName}-proof-report.json`, payload);
              downloadTextFile(`${baseName}-proof-certificate.txt`, buildProofCertificateText(proofReport, exportContext));
            }}
          />
          </div>
        </div>
      </div>

      {showTour && (
        <div className="tour-overlay" role="dialog" aria-modal="true">
          <div className="tour-card">
            <div className="tour-header">
              <span className="tour-pill">60s tour</span>
              <button className="tour-close" onClick={handleCloseTour} aria-label="Close tour">×</button>
            </div>
            <div className="tour-step-count">Step {tourStep + 1} / {TOUR_STEPS.length}</div>
            <h3>{TOUR_STEPS[tourStep].title}</h3>
            <p>{TOUR_STEPS[tourStep].body}</p>
            <div className="tour-actions">
              <button className="qs-btn ghost" onClick={handleCloseTour}>Skip</button>
              <div className="tour-nav">
                <button className="qs-btn" onClick={handlePrevTour} disabled={tourStep === 0}>Prev</button>
                {tourStep === TOUR_STEPS.length - 1 ? (
                  <button className="qs-btn" onClick={handleCloseTour}>Done</button>
                ) : (
                  <button className="qs-btn" onClick={handleNextTour}>Next</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast toast-${toast.type || 'info'}`}>
          <span>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <UpgradeModal
        open={upgradeModalOpen}
        usage={usage}
        message={upgradeMessage}
        loading={checkoutLoading}
        checkoutError={checkoutError}
        badge={modalMode === 'login' ? 'Guest Trial Complete' : 'Upgrade Required'}
        title={modalMode === 'login' ? 'Continue with Google to keep redacting' : 'You reached your free redaction limit'}
        actionLabel={modalMode === 'login' ? 'Continue with Google' : 'Upgrade with Stripe'}
        secondaryLabel={modalMode === 'login' ? 'Stay in guest mode' : 'Maybe later'}
        onClose={() => {
          if (!checkoutLoading) {
            setUpgradeModalOpen(false);
            setCheckoutError('');
            setModalMode('upgrade');
          }
        }}
        onUpgrade={handleUpgradeCheckout}
      />
      {logDrawerOpen && (
        <div className="log-drawer" role="dialog" aria-label="Activity log">
          <div className="log-drawer-header">
            <strong>Activity log</strong>
            <button type="button" onClick={() => setLogDrawerOpen(false)}>×</button>
          </div>
          <div className="log-drawer-body">
            {logs.length === 0 ? (
              <p className="log-empty">No activity yet.</p>
            ) : (
              <ul>
                {logs.map((log) => (
                  <li key={log.id || `${log.ts}-${log.action}`}>
                    <div className="log-line">
                      <span className="log-action">{log.action}</span>
                      <span className="log-file">{log.file || '—'}</span>
                    </div>
                    <div className="log-meta">{log.user || 'user'} • {new Date(log.ts).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
export default RedactionApp; 
